/**
 * @fileoverview Okta SDK Configuration for Autolabs
 *
 * Configures @okta/okta-auth-js for SPA authentication with PKCE.
 * This configuration is used by both the OktaAuth instance and the Vue plugin.
 */

import { OktaAuth } from '@okta/okta-auth-js'

export const LOCAL_ONLY_AUTH_GATE_SENTINEL = 'LOCAL_ONLY_DO_NOT_USE_IN_DEV_OR_PROD'
export const isLocalOnlyAuthEnabled =
  import.meta.env.DEV &&
  import.meta.env.VITE_LOCAL_ONLY_AUTH_GATE === LOCAL_ONLY_AUTH_GATE_SENTINEL

/**
 * Okta SDK configuration
 * @type {import('@okta/okta-auth-js').OktaAuthOptions}
 */
export const oktaConfig = {
  // Identity Provider
  issuer: import.meta.env.VITE_OKTA_ISSUER,
  clientId: import.meta.env.VITE_OKTA_CLIENT_ID,

  // Redirect URIs
  redirectUri: `${window.location.origin}/login/callback`,
  postLogoutRedirectUri: window.location.origin,

  // OAuth settings
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  pkce: true, // REQUIRED for SPA
  responseType: 'code',

  // Token management
  tokenManager: {
    autoRenew: true, // Automatically refresh tokens
    autoRemove: true, // Remove expired tokens
    secure: true, // Use secure cookies where available
    storage: 'sessionStorage', // More secure than localStorage
    expireEarlySeconds: 120 // Renew 2 minutes before expiration
  },

  // Services
  services: {
    autoRenew: true,
    syncStorage: true // Sync across tabs
  },

  // Restore original URI after login
  restoreOriginalUri: async (oktaAuth, originalUri) => {
    // Router will handle redirect after callback
    window.location.replace(originalUri || '/')
  }
}

const noopAuthState = {
  isAuthenticated: false,
  accessToken: null
}

const noopOktaAuth = {
  authStateManager: {
    getAuthState: () => noopAuthState,
    subscribe: () => {},
    unsubscribe: () => {}
  },
  getAccessToken: async () => null,
  signInWithRedirect: async () => {},
  signOut: async () => {},
  start: () => {},
  stop: () => {},
  tokenManager: {
    renew: async () => null,
    on: () => {},
    off: () => {}
  }
}

/**
 * Create singleton OktaAuth instance
 */
export const oktaAuth = isLocalOnlyAuthEnabled ? noopOktaAuth : new OktaAuth(oktaConfig)

// Start services (auto-renew, etc.)
if (!isLocalOnlyAuthEnabled) {
  oktaAuth.start()
}

export default oktaAuth
